import React, { useState, useEffect } from 'react';
import { get, map, find } from 'lodash';
import moment, { Moment } from 'moment';
import { getDateTimeFromSelectedTimeSlice } from 'components/mentoring/ClassScheduleModal';
import { AvailableSchedule } from 'components/user/signup/SignupAvailableClassSchedule';
import { getAvailableSchedule, updateAvailableSchedule } from 'libs/api/mentoring';
import { useDataApi, useTimezone } from 'libs/hooks';
import { showNotification } from 'libs/utils';
import { TAvailableSchedule, TSelectedTimeSlice } from 'modules/mentoring';
import { TMentor, TScheduleStatus, TBeforeFormattedScheduleStatus } from 'modules/user';
import { CDateFormat, CDefaultDateTimeFormat } from 'constants/base.const';
import { EScheduleStatus } from 'enums/mentoring.enum';
import { Col, Spin, Row, Modal, Button, DatePicker, Typography, Tag, Checkbox } from 'antd';
import { RightOutlined, LeftOutlined } from '@ant-design/icons';
import { SecondaryColor, PrimaryColor } from 'GlobalStyles';

const { Title, Text } = Typography;
type TAvailableScheduleModalProps = {
  mentor: TMentor;
  visible: boolean;
  setVisible: any;
};
const AvailableScheduleModal = ({ mentor, visible, setVisible }: TAvailableScheduleModalProps) => {
  const [submitButtonDisabled, setSubmitButtonDisabled] = useState(false);
  const [getDateTime, timezone] = useTimezone();
  const today = getDateTime('');
  const [currentDate, setCurrentDate] = useState(today);
  const [selectedTimeSlices, setSelectedTimeSlices] = useState<Array<TSelectedTimeSlice>>([]);
  const [startDateOfWeek, setStartDateOfWeek] = useState(currentDate.clone().day('Sunday'));
  //오늘을 기준으로 오늘 이전의 availableSchedule은 받지 않기 위해
  const getPromise = getAvailableSchedule.bind(null, get(mentor, 'id'), {
    date_from: today
      .clone()
      .add(-1, 'days')
      .format(CDateFormat),
    date_to: undefined,
  });
  const [{ data, loading }, setCallback, setExecuting] = useDataApi<Array<TAvailableSchedule>>(
    getPromise,
    [],
    false,
  );
  // 스케줄 타임별 row 그려주는 state
  const [tableRows, setTableRows] = useState<Array<TAvailableSchedule>>([]);
  const [isChecked, setIsChecked] = useState<boolean>();
  const autoUpdatePeriod = 10;
  const [availableDate, setAvailableDate] = useState<Array<string>>();
  const [unavailableSchedule, setUnavailableSchedule] = useState<any>();

  // tableRows ==>[{time_slice:-2}, time_slice:-1}, ~, time_slice:48}]
  // selectedTimeSlices ==> 기존에 선택했던 스케줄 중에 가능한 스케줄만(멘토링 예약되지 않은 스케줄) [{time_slice: 0, date_at: "2021-09-04"}, ]

  //체크박스 선택하고 수정할 때 '예약대기', '예약마감'도 선택할 수 있게 한 뒤 submit 할 때는 이 일정 빼고 보내기
  function filterAlreadyUnavailable(
    selecdAvailableSchedules: Array<{ datetime_at: string; status: string }>,
  ) {
    const formattedAlreadyUnavailable: any = [];

    unavailableSchedule?.forEach((el: any) => {
      const keys = Object.keys(el);
      const YYYY_MM_DD_Arr = keys.filter((key: string) => key !== 'time_slice');
      YYYY_MM_DD_Arr.forEach((YYYY_MM_DD: string) => {
        const unavailableObj = {
          time_slice: el.time_slice,
          date_at: YYYY_MM_DD,
        };
        const formattedTime = getDateTimeFromSelectedTimeSlice(unavailableObj, timezone).format(
          CDefaultDateTimeFormat,
        );
        formattedAlreadyUnavailable.push({
          time_slice: el.time_slice,
          datetime_at: formattedTime,
        });
      });
    });

    for (let i = 0; i < formattedAlreadyUnavailable.length; i++) {
      for (let x = 0; x < selecdAvailableSchedules.length; x++) {
        if (selecdAvailableSchedules[x].datetime_at === formattedAlreadyUnavailable[i].datetime_at) {
          selecdAvailableSchedules.splice(x, 1);
        }
      }
    }

    //기존에 불가능했던 스케줄틀을 필터링한 바디를 만듦
    return selecdAvailableSchedules;
  }

  //(현재 시점보다 이전 & 이번주 내)에 수업 완료한 일정 등 available 이외의 상태값을 가지고 있는 경우의 수를 제외하기 위해
  function AvailableSchedulesAfterCurrentTime(
    selecdAvailableSchedules: Array<{ datetime_at: string; status: string }>,
  ) {
    const formattedCurrentTime = getDateTime('').format(CDefaultDateTimeFormat);
    const filterBeforeCurrentTime = selecdAvailableSchedules.filter(
      (schedule: { datetime_at: string; status: string }) =>
        moment(schedule.datetime_at) > moment(formattedCurrentTime),
    );
    return filterBeforeCurrentTime;
  }

  function handleCancel() {
    setVisible(false);
    setAvailableDate([]);
  }

  //submit 할 때 - 이번주 가능일정만 필터링
  function remainAvailableOnlyThisWeek(selected: Array<TSelectedTimeSlice>) {
    //[ {datetime_at: Moment, status: "available"}, {datetime_at: Moment, status: "available"}]
    const availableSchedules = selected.map((elem: TSelectedTimeSlice) => ({
      datetime_at: getDateTimeFromSelectedTimeSlice(elem, timezone),
      // .format(CDefaultDateTimeFormat), //포메팅 하기전!! Moment의 상태 유지
      status: EScheduleStatus.Available,
    }));
    const startOfTheWeek = today.clone().day('Sunday'); //_d: Sun Sep 05 2021 09:00:00 GMT+0900 (한국 표준시)
    const endOfTheWeek = today
      .clone()
      .day('Sunday')
      .add(7, 'd'); //_d: Sun Sep 12 2021 09:00:00 GMT+0900 (한국 표준시)
    const availableSchedulesThisWeek = availableSchedules.filter((el: TBeforeFormattedScheduleStatus) => {
      return startOfTheWeek <= el.datetime_at && el.datetime_at < endOfTheWeek;
    });

    return availableSchedulesThisWeek;
  }

  //submit 할 때 - 체크박스 체크 후 가능일정 post할 때의 params //submit할 경우
  function makeAvailableForMultipleWeeks(selected: Array<TSelectedTimeSlice>, period: number) {
    //체크박스 체크하고 다른 주의 일정을 선택할 수도 있기 때문에 이번주 일정만 필터링 해서 이번주와 동일한 일정으로 X 10주
    const availableSchedulesThisWeek = remainAvailableOnlyThisWeek(selected);
    const autoUpdatedAvailableSchedule = [];

    for (let i = 0; i < availableSchedulesThisWeek.length; i++) {
      for (let x = 0; x < period; x++) {
        const formattedAvailableSchedules = {
          datetime_at: availableSchedulesThisWeek[i].datetime_at
            .clone()
            .add(7 * x, 'day')
            .format(CDefaultDateTimeFormat),
          status: EScheduleStatus.Available,
        };
        autoUpdatedAvailableSchedule.push(formattedAvailableSchedules);
      }
    }
    return autoUpdatedAvailableSchedule;
  }

  //체크박스 조작할 때 - 체크 해제했을 경우 이번주만 체크되어 있는걸로하기위해
  function selectedTimeSlicesForThisWeek(selected: Array<TSelectedTimeSlice>) {
    const startOfTheWeek = today.clone().day('Sunday'); //_d: Sun Sep 05 2021 09:00:00 GMT+0900 (한국 표준시)
    const endOfTheWeek = today
      .clone()
      .day('Sunday')
      .add(7, 'd'); //_d: Sun Sep 12 2021 09:00:00 GMT+0900 (한국 표준시)

    //이번주에 선택한 일정들로만
    const availableSchedules = selected.map((elem: TSelectedTimeSlice) => ({
      ...elem,
      date_at: getDateTimeFromSelectedTimeSlice(elem, timezone),
    }));
    const availableSchedulesThisWeek = availableSchedules.filter(
      (el: { time_slice: number; date_at: Moment }) => {
        return startOfTheWeek <= el.date_at && el.date_at < endOfTheWeek;
      },
    );

    return availableSchedulesThisWeek;
  }

  //체크박스 조작할 때 -selectedTimeSlices를 일정 기간 반복한 데이터로 만들어주기
  function selectedTimeSlicesForMultipleWeeks(selected: Array<TSelectedTimeSlice>) {
    const availableSchedulesThisWeek = selectedTimeSlicesForThisWeek(selected);
    //10주이후까지 보여주기
    const autoUpdatedAvailableSchedule = [];
    for (let i = 0; i < availableSchedulesThisWeek.length; i++) {
      for (let x = 0; x < autoUpdatePeriod; x++) {
        const formattedAvailableSchedules = {
          time_slice: availableSchedulesThisWeek[i].time_slice,
          date_at: availableSchedulesThisWeek[i].date_at
            .clone()
            .add(7 * x, 'day')
            .format('YYYY-MM-DD'),
        };
        autoUpdatedAvailableSchedule.push(formattedAvailableSchedules);
      }
    }

    return autoUpdatedAvailableSchedule;
  }

  async function handleSubmit() {
    try {
      const query = { auto_time_slot: String(isChecked) };

      if (isChecked) {
        setSubmitButtonDisabled(true);
        const availableForTenWeeks = makeAvailableForMultipleWeeks(selectedTimeSlices, autoUpdatePeriod);
        //체크된 상태에서 이번주에 예약성사된 것 등 불가능한 일정 있을 때 그거 빼고 바디로 보냄(기존에 예약되어 있던거가 available로 가면 에러)
        const filterAlreadyUnavailableSchedules = filterAlreadyUnavailable(availableForTenWeeks);
        //(현재 시점보다 이전 & 이번주 내)에 수업 완료한 일정 등 available 이외의 상태값을 가지고 있는 경우의 수를 제외하기 위해
        const AvailableSchedulesAfterCurrent = AvailableSchedulesAfterCurrentTime(
          filterAlreadyUnavailableSchedules,
        );

        await updateAvailableSchedule(AvailableSchedulesAfterCurrent as Array<TScheduleStatus>, query);
      } else {
        setSubmitButtonDisabled(true);
        const availableSchedules = map(selectedTimeSlices, (elem: TSelectedTimeSlice) => ({
          datetime_at: getDateTimeFromSelectedTimeSlice(elem, timezone).format(CDefaultDateTimeFormat),
          status: EScheduleStatus.Available,
        }));
        await updateAvailableSchedule(availableSchedules as Array<TScheduleStatus>, query);
      }

      showNotification('success', '수업 가능 일정이 수정되었습니다');
      setVisible(false);
    } catch (e) {
      throw e;
    } finally {
      setSubmitButtonDisabled(false);
    }
  }

  async function onCheck(e: any) {
    //모든 경우에 실행
    setIsChecked(e.target.checked);

    //기존에는 체크박스 선택(true)하고 일정 선택했었다가 false로 바꾸는 경우  ==> 이번주만 남겨놓기
    if (!e.target.checked) {
      //이번주만 남겨놓고 필터링 하여 post
      //매번 post get 안해도 되도록 ==> 달력 초록색으로 표시되도록 상태값 관리
      const formattedSelectedTimeSlices = selectedTimeSlicesForThisWeek(selectedTimeSlices).map((el: any) => {
        return { ...el, date_at: el.date_at.format('YYYY-MM-DD') };
      });
      setSelectedTimeSlices(formattedSelectedTimeSlices);
    } else if (e.target.checked) {
      // post get 안해도 되도록
      setSelectedTimeSlices(selectedTimeSlicesForMultipleWeeks(selectedTimeSlices));
    }
  }

  function onPanelChangeWeek(value: Moment, mode: string) {
    setCurrentDate(value);
  }

  function onChangeWeek(date: Moment | null, dateString: string) {
    if (date != null) {
      setCurrentDate(date);
    }
  }

  function makeUniqueElement(arr: Array<any>) {
    const uiqueNewAvailableDate = arr.filter((element: string, index: number) => {
      return arr.indexOf(element) === index;
    });
    return uiqueNewAvailableDate;
  }

  useEffect(() => {
    // 최초 모달이 visible될때 가능한 스케줄 정보를 받아옴
    if (visible) {
      setCallback(() => getPromise);
      setExecuting(true);
    }
  }, [visible]);

  useEffect(() => {
    if (!loading && data) {
      //Array<{time_slice:number}>
      //[{time_slice:-2}, {time_slice:-1}, ~, time_slice:48}]
      setTableRows([
        { time_slice: -2 },
        { time_slice: -1 },

        //24시간을 30분 단위로 쪼개서 48개의 요소를 만들어내야 하기 떄문
        // *** 0~47 값들이 인덱스로 들어가면서 불가능한 일정이 있는 time_slice는 {time_slice: 0, 2021-09-11: 'reserved'} 이런식으로 리턴 & 불가능한 일정이 없는 time_slice는 {time_slice: 5} 이렇게 리턴
        ...map(Array(48), (v, index) => {
          //lodash find - 조건을 만족하는 컬렉션에서의 첫번째 요소를 찾는 메소드 (배열에서 요소를 비교하면서 일치하는 첫번쨰 요소만을 객체 형태로반환)
          //lodash get - 만족하는 요소가 없으면 디폴트값 []
          const rowData = find(get(data, 'schedules_unavailable', []), (elem) => {
            return get(elem, 'time_slice') === index;
          });
          if (rowData) {
            return rowData;
          } else {
            return { time_slice: index };
          }
        }),

        { time_slice: 48 },
      ]);

      //가능한 스케줄
      setSelectedTimeSlices(
        map(get(data, 'schedules_available', []), (elem) => {
          return {
            time_slice: get(elem, 'time_slice'),
            date_at: get(elem, 'date_at'),
          };
        }),
      );
    }
  }, [loading]);

  useEffect(() => {
    setIsChecked(get(data, 'auto_slot_open') === 'true');
  }, [data]);

  useEffect(() => {
    // 현재 날짜가 바뀌면 그날이 포함된 주의 일요일로 focus 이동
    if (
      currentDate
        .clone()
        .day('Sunday')
        .week() != startDateOfWeek.week()
    ) {
      setStartDateOfWeek(currentDate.clone().day('Sunday'));
    }
  }, [currentDate]);

  useEffect(() => {
    const newAvailableDate = [...selectedTimeSlices.map((el: TSelectedTimeSlice) => el.date_at)];
    const uiqueNewAvailableDate = makeUniqueElement(newAvailableDate);
    setAvailableDate([...uiqueNewAvailableDate]);
  }, [data, selectedTimeSlices, isChecked]);

  useEffect(() => {
    setUnavailableSchedule(get(data, 'schedules_unavailable', []));
  }, [data]);

  return (
    <Modal
      visible={visible}
      maskClosable={true}
      closable={false}
      footer={
        <Row justify='end'>
          <Button onClick={handleCancel}>닫기</Button>
          <Button type='primary' onClick={handleSubmit}>
            저장
          </Button>
        </Row>
      }
      width={940}
      style={{ top: 10 }}
      onCancel={handleCancel}
    >
      <Row justify='space-between'>
        <Col style={{ height: '130px' }}>
          <span style={{ color: `${SecondaryColor}` }}>{timezone}</span>
          <div style={{ fontWeight: 'bold', fontSize: '30px' }}>
            {currentDate.year()}년 {currentDate.month() + 1}월
          </div>
          <div style={{ fontSize: '18px' }}>
            <Tag
              className='arrow-box'
              onClick={() => {
                setCurrentDate(currentDate.clone().add(-7, 'd'));
              }}
            >
              <LeftOutlined />
            </Tag>
            <Tag
              style={{
                background: `${PrimaryColor}`,
                color: '#fff',
                borderRadius: '8px',
                height: '26px',
                lineHeight: '26px',
                padding: '0px 10px',
                fontSize: '13px',
                cursor: 'pointer',
              }}
              onClick={() => {
                setCurrentDate(today);
              }}
            >
              오늘
            </Tag>
            <Tag
              className='arrow-box'
              onClick={() => {
                setCurrentDate(currentDate.clone().add(7, 'd'));
              }}
            >
              <RightOutlined />
            </Tag>
          </div>
        </Col>
        <Col style={{ height: '130px' }}>
          <DatePicker
            open={visible}
            onChange={onChangeWeek}
            onPanelChange={onPanelChangeWeek}
            picker='week'
            dropdownClassName='inline-datepicker-popup'
            value={currentDate}
            style={{ visibility: 'hidden', width: '300px' }}
            getPopupContainer={(trigger) => trigger.parentElement as HTMLElement}
            // 오늘(today의 시작시점)보다 이전 시점는 disabledDate
            disabledDate={(current: Moment) => {
              return current && current < today.startOf('day');
            }}
            // disabledDate={(current:Moment) => {return current && current < today.startOf('week')}}

            dateRender={(current) => {
              // 예약 가능한 일정이 있는 날짜는 강조표시
              const style = {} as { color: string };
              // data.available_dates   ==> ["2021-09-03", "2021-09-04"]
              const isAvailable = find(availableDate, (available_at) => {
                //available_at => 2021-09-04
                // current=> 달력에 나오는 날짜 모두  => Mon Aug 30 2021 02:00:41 GMT+0900 (한국 표준시) ~ Sun Oct 10 2021 02:00:41 GMT+0900   (type= Moment)
                return current.format(CDateFormat) === available_at;
              });
              //열어둔 슬랏(available)있으면 녹색 컬러
              if (isAvailable) {
                style.color = `${SecondaryColor}`;
              }
              return (
                <div className='ant-picker-cell-inner' style={style}>
                  {current.date()}
                </div>
              );
            }}
          />
        </Col>
      </Row>

      <Row>
        <Checkbox onChange={onCheck} style={{ marginTop: '-10px' }} checked={isChecked}>
          이번주에 선택한 일정을 매주 반복하기
        </Checkbox>
      </Row>

      <Spin tip='loading...' spinning={loading}>
        <AvailableSchedule
          selectedTimeSlices={selectedTimeSlices}
          setSelectedTimeSlices={setSelectedTimeSlices}
          startDateOfWeek={startDateOfWeek}
          tableRows={tableRows}
          isChecked={isChecked}
          selectedTimeSlicesForMultipleWeeks={selectedTimeSlicesForMultipleWeeks}
          unavailableSchedule={unavailableSchedule}
        />
      </Spin>
    </Modal>
  );
};

AvailableScheduleModal.defaultProps = {};

export default AvailableScheduleModal;
